export async function optimiseImageForUpload(file, options = {}) {
  const {
    maxInputBytes = 8 * 1024 * 1024,
    maxDimension = 1800,
    outputType = "image/jpeg",
    qualityStart = 0.9,
    minQuality = 0.7,
    targetBytes = 900 * 1024,
  } = options;

  if (!file) throw new Error("No image provided.");
  if (!(file.type || "").startsWith("image/")) throw new Error("Please upload an image file.");
  if (file.size > maxInputBytes) {
    throw new Error(`Image must be smaller than ${Math.round(maxInputBytes / (1024 * 1024))} MB.`);
  }

  const srcUrl = await readFileAsDataUrl(file);
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not read the image."));
    image.src = srcUrl;
  });

  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process the image.");
  ctx.drawImage(img, 0, 0, width, height);

  let quality = qualityStart;
  let blob = await canvasToBlob(canvas, outputType, quality);
  while (blob.size > targetBytes && quality > minQuality) {
    quality = Math.max(minQuality, quality - 0.08);
    blob = await canvasToBlob(canvas, outputType, quality);
    if (quality === minQuality) break;
  }

  const ext = outputType === "image/webp" ? "webp" : "jpg";
  return new File([blob], replaceExtension(file.name, ext), { type: outputType });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not generate the processed image."));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the image."));
    reader.readAsDataURL(file);
  });
}

function replaceExtension(filename, nextExt) {
  const name = String(filename || "image").replace(/\.[A-Za-z0-9]+$/, "");
  return `${name}.${nextExt}`;
}
